import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import * as Icons from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import { DashboardLayout } from "component/layout/dashboard";
import { FullPageCard } from "component/shared/full-page-card";
import { FeatherIcon } from "component/shared/feather-icon";
import { ColdFusionIframe } from "component/shared/iframe/coldfusion";
import { PageProps } from "shared/page-props";
import * as Router from "react-router-dom";
import { RootState } from "redux/store";
import { getEmailRoutingRules } from "redux/selector";
import { fetchEmailRoutingRules, saveEmailRoutingRules } from "redux/slice/email-routing-rules";
import { RefreshedLeadActivity } from "./refreshed";
import { EmailRoutingRuleResource } from "api/resource/email-routing-rule";
import * as env from "shared/env";
import { coldFusionReplatform } from "shared/env";

interface Props extends PageProps, Router.RouteComponentProps {}

interface RuleState {
	mainContactYn: boolean;
	agentYn: boolean;
	listingAgentYn: boolean;
	otherYn: boolean;
	otherEmail: string;
}

type SectionState = { [emailMasterId: number]: RuleState };

// CF DesignController.cfc:59-68 — the Other-email field caps at maxlength=200 (EmailRouting.otherEmail
// DB column is 200) and, when non-empty, must be a valid email. CF normalizes separators (; : whitespace)
// to commas then validates; we validate each comma token individually (matching the server backstop
// isOtherEmailValid, and fixing CF's single-string check for legitimate multi-address entries).
const OTHER_EMAIL_MAX_LENGTH = 200;
const OTHER_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const OTHER_EMAIL_ERROR = "Error:Email address in 'OTHER' box is incorrectly formatted";

function isOtherEmailValid(value: string): boolean {
	const normalized = value.trim().replace(/[;:\s]/g, ",");
	return normalized.split(",").every((token) => {
		const candidate = token.trim();
		return candidate === "" || OTHER_EMAIL_REGEX.test(candidate);
	});
}

function ruleToState(rule: EmailRoutingRuleResource): RuleState {
	return {
		mainContactYn: rule.mainContactYn,
		agentYn: rule.agentYn,
		listingAgentYn: rule.listingAgentYn,
		otherYn: rule.otherYn,
		otherEmail: rule.otherEmail || "",
	};
}

function rulesToSectionState(rules: EmailRoutingRuleResource[]): SectionState {
	const state: SectionState = {};
	for (const rule of rules) {
		state[rule.emailMasterId] = ruleToState(rule);
	}
	return state;
}

interface RuleRowProps {
	rule: EmailRoutingRuleResource;
	state: RuleState;
	onChange: (emailMasterId: number, patch: Partial<RuleState>) => void;
}

const RuleRow: React.FC<RuleRowProps> = ({ rule, state, onChange }) => {
	return (
		<Mui.Grid container direction="column" spacing={1}>
			<Mui.Grid item>
				<Mui.Typography variant="h6">
					{rule.shortDesc}
				</Mui.Typography>
			</Mui.Grid>
			<Mui.Grid item>
				<Mui.Grid container spacing={0} direction="column">
					{rule.useMainContactYn && (
						<Mui.Grid item>
							<Mui.FormControlLabel
								control={
									<Mui.Checkbox
										checked={state.mainContactYn}
										onChange={(e) => onChange(rule.emailMasterId, { mainContactYn: e.target.checked })}
										color="primary"
										size="small"
									/>
								}
								label="Main Company Contact"
							/>
						</Mui.Grid>
					)}
					{rule.useAgentYn && (
						<Mui.Grid item>
							<Mui.FormControlLabel
								control={
									<Mui.Checkbox
										checked={state.agentYn}
										onChange={(e) => onChange(rule.emailMasterId, { agentYn: e.target.checked })}
										color="primary"
										size="small"
									/>
								}
								label={rule.useListingAgentYn ? "Agent selected by User" : "Agent, if selected"}
							/>
						</Mui.Grid>
					)}
					{rule.useListingAgentYn && (
						<Mui.Grid item>
							<Mui.FormControlLabel
								control={
									<Mui.Checkbox
										checked={state.listingAgentYn}
										onChange={(e) => onChange(rule.emailMasterId, { listingAgentYn: e.target.checked })}
										color="primary"
										size="small"
									/>
								}
								label="Listing Agent, if an office listing"
							/>
						</Mui.Grid>
					)}
					{/* CF emailRouting1.cfm:150-152 — "Other" checkbox and its email text field sit
					    inline on one line, and the field is always visible (not gated on the checkbox). */}
					<Mui.Grid item>
						<Mui.Box display="flex" alignItems="center" flexWrap="wrap">
							<Mui.FormControlLabel
								control={
									<Mui.Checkbox
										checked={state.otherYn}
										onChange={(e) => onChange(rule.emailMasterId, { otherYn: e.target.checked })}
										color="primary"
										size="small"
									/>
								}
								label="Other"
							/>
							<Mui.TextField
								value={state.otherEmail}
								onChange={(e) => onChange(rule.emailMasterId, { otherEmail: e.target.value })}
								size="small"
								variant="outlined"
								placeholder="Email address"
								inputProps={{ maxLength: OTHER_EMAIL_MAX_LENGTH }}
								style={{ minWidth: 280 }}
							/>
						</Mui.Box>
					</Mui.Grid>
				</Mui.Grid>
			</Mui.Grid>
		</Mui.Grid>
	);
};

interface SectionProps {
	title: string;
	description: React.ReactNode;
	rules: EmailRoutingRuleResource[];
	onSave: (sectionState: SectionState) => void;
	saving: boolean;
}

const RoutingSection: React.FC<SectionProps> = ({ title, description, rules, onSave, saving }) => {
	const [sectionState, setSectionState] = useState<SectionState>(() => rulesToSectionState(rules));
	const [dirty, setDirty] = useState(false);
	const rulesKey = rules.map((r) => `${r.emailMasterId}:${r.mainContactYn}:${r.agentYn}:${r.listingAgentYn}:${r.otherYn}:${r.otherEmail}`).join("|");

	useEffect(() => {
		setSectionState(rulesToSectionState(rules));
		setDirty(false);
	}, [rulesKey]);

	const handleChange = (emailMasterId: number, patch: Partial<RuleState>) => {
		setSectionState((prev) => ({
			...prev,
			[emailMasterId]: { ...prev[emailMasterId], ...patch },
		}));
		setDirty(true);
	};

	const handleCancel = () => {
		setSectionState(rulesToSectionState(rules));
		setDirty(false);
	};

	if (rules.length === 0) return null;

	// CF renders each section as its own widget-box (emailRouting1.cfm:45-78, 81-111) — one card per section.
	return (
		<Mui.Box mb={2}>
			<FullPageCard>
				<Mui.Grid container spacing={3} direction="column">
					<Mui.Grid item>
						<Mui.Typography variant="h6">{title}</Mui.Typography>
					</Mui.Grid>
					<Mui.Grid item>
						<Mui.Typography variant="body2">{description}</Mui.Typography>
					</Mui.Grid>
					{rules.map((rule, index) => (
						<React.Fragment key={rule.emailMasterId}>
							<Mui.Grid item>
								<RuleRow
									rule={rule}
									state={sectionState[rule.emailMasterId] || ruleToState(rule)}
									onChange={handleChange}
								/>
							</Mui.Grid>
							{index < rules.length - 1 && (
								<Mui.Grid item>
									<Mui.Divider />
								</Mui.Grid>
							)}
						</React.Fragment>
					))}
					<Mui.Grid item>
						<Mui.Grid container spacing={1}>
							<Mui.Grid item>
								<Mui.Button
									color="primary"
									variant="contained"
									size="small"
									onClick={() => onSave(sectionState)}
									disabled={saving || !dirty}
								>
									Save
								</Mui.Button>
							</Mui.Grid>
							<Mui.Grid item>
								<Mui.Button
									variant="outlined"
									size="small"
									onClick={handleCancel}
									disabled={saving || !dirty}
								>
									Cancel
								</Mui.Button>
							</Mui.Grid>
						</Mui.Grid>
					</Mui.Grid>
				</Mui.Grid>
			</FullPageCard>
		</Mui.Box>
	);
};

const ColdFusionFallback: React.FC<Props> = ({ user }) => {
	const title = "Email Advanced Routing";
	return (
		<DashboardLayout
			permitted={user && (!user.permissions.oneCrm || user.admin)}
			title={title}
			header={
				<Mui.Typography variant="h1">
					<FeatherIcon>
						<Icons.Settings />
					</FeatherIcon>
					{title}
				</Mui.Typography>
			}
			iframe={<ColdFusionIframe url="/emailRouting.cfm" />}
		/>
	);
};

const NativePage: React.FC<Props> = ({ user }) => {
	const dispatch = useDispatch();
	const rules = useSelector((state: RootState) => getEmailRoutingRules(state));
	const loading = useSelector((state: RootState) => state.emailRoutingRules.loading);
	const [saving, setSaving] = useState(false);
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState("");
	const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");

	useEffect(() => {
		dispatch(fetchEmailRoutingRules() as any);
	}, [dispatch]);

	const handleSave = async (sectionRules: EmailRoutingRuleResource[], sectionState: SectionState) => {
		const payload = sectionRules
			.filter((rule) => !!sectionState[rule.emailMasterId])
			.map((rule) => {
				const state = sectionState[rule.emailMasterId];
				return {
					emailMasterId: rule.emailMasterId,
					mainContactYn: state.mainContactYn,
					agentYn: state.agentYn,
					listingAgentYn: state.listingAgentYn,
					otherYn: state.otherYn,
					otherEmail: state.otherEmail || null,
				};
			});
		// CF blocks the save and shows the error when a selected Other row has a malformed address
		// (DesignController.cfc:59-68). Mirror it client-side (server 400s as a backstop).
		const hasInvalidOther = payload.some(
			(rule) => rule.otherYn && rule.otherEmail && !isOtherEmailValid(rule.otherEmail)
		);
		if (hasInvalidOther) {
			setSnackbarMessage(OTHER_EMAIL_ERROR);
			setSnackbarSeverity("error");
			setSnackbarOpen(true);
			return;
		}
		setSaving(true);
		try {
			await dispatch(saveEmailRoutingRules(payload) as any);
			setSnackbarMessage("Email Routing Changes Saved Successfully");
			setSnackbarSeverity("success");
			setSnackbarOpen(true);
		} catch{
			setSnackbarMessage("Failed to save email routing settings");
			setSnackbarSeverity("error");
			setSnackbarOpen(true);
		} finally {
			setSaving(false);
		}
	};

	const subscriberRules = rules
		.filter((r) => r.dependencyClassName === "ClientList" && r.showInAdminYn)
		.sort((a, b) => a.displayOrder - b.displayOrder);

	const clientRules = rules
		.filter((r) => r.dependencyClassName === "Reseller" && r.showInAdminYn)
		.sort((a, b) => a.displayOrder - b.displayOrder);

	const title = "Email Advanced Routing";

	return (
		<DashboardLayout
			permitted={user && (!user.permissions.oneCrm || user.admin)}
			title={title}
			header={
				<Mui.Typography variant="h1">
					<FeatherIcon>
						<Icons.Settings />
					</FeatherIcon>
					{title}
				</Mui.Typography>
			}
		>
			{loading && rules.length === 0 ? (
				<FullPageCard>
					<Mui.Box display="flex" justifyContent="center" p={4}>
						<Mui.CircularProgress />
					</Mui.Box>
				</FullPageCard>
			) : (
				<>
					<RoutingSection
						title="Get Copies of Emails Sent to Leads"
						description={
							<>
								{/* CF bold-italic lead sentence (emailRouting1.cfm:54). */}
								<em><strong>Route COPIES of emails sent to leads to you and your agents:</strong></em>
								<br /><br />
								If you would like to receive copies of these messages, use the settings below to determine <strong>who should receive copies of these messages</strong>. You can select multiple recipients for each email type. If no recipients are selected, the email will be sent to the lead.
								<br /><br />
								If the Agent checkbox is selected (if available) then the agent assigned to the lead will receive a copy. If the lead does not have an agent assigned, no agent copy will be sent. To view and configure your Main Company Contact email address, click on the <strong>Email Basic Setup</strong> section.
							</>
						}
						rules={subscriberRules}
						onSave={(sectionState) => handleSave(subscriberRules, sectionState)}
						saving={saving}
					/>
					{/* Section 2 visibility is server-driven: the GET payload only returns the "Reseller"
					    (EmailMaster) rows when the client is eligible (java-supp's Fusion/reseller
					    gating). No client-side oneCrm check — gate on data presence. */}
					{clientRules.length > 0 && (
						<RoutingSection
							title="Options for Emails Sent to You"
							description={
								<>
									{/* CF bold-italic lead sentence (emailRouting1.cfm:90). */}
									<em><strong>Configure custom routing of emails sent to you and your agents:</strong></em>
									<br /><br />
									Use the settings below to specify alternate or additional recipients of these messages. You can select multiple recipients for each email type. If no check boxes are selected, messages will be sent to the address designated as the Main Company Contact for your account. To view and configure your Main Company Contact email address, click on the <strong>Email Basic Setup</strong> section.
								</>
							}
							rules={clientRules}
							onSave={(sectionState) => handleSave(clientRules, sectionState)}
							saving={saving}
						/>
					)}
				</>
			)}
			<Mui.Snackbar
				open={snackbarOpen}
				autoHideDuration={4000}
				onClose={() => setSnackbarOpen(false)}
				action={
					<Mui.IconButton
						size="small"
						aria-label="close"
						color="inherit"
						onClick={() => setSnackbarOpen(false)}
					>
						<Icons.X fontSize="small" />
					</Mui.IconButton>
				}
			>
				<MuiAlert severity={snackbarSeverity}>
					<Mui.Typography>{snackbarMessage}</Mui.Typography>
				</MuiAlert>
			</Mui.Snackbar>
		</DashboardLayout>
	);
};

export const SettingsEmailRoutingPage: React.FC<Props> = (props) => {
	// MAX-3948: the refreshed Lead Activity page. Off by default until it ships with MAX-3951
	// and MAX-3952, so the existing pages keep rendering unchanged.
	if (env.notificationsRefresh) {
		return <RefreshedLeadActivity {...props} />;
	}
	if (!coldFusionReplatform) {
		return <ColdFusionFallback {...props} />;
	}
	return <NativePage {...props} />;
};
